"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const uuid_1 = require("@lukeed/uuid");
const nmr_correlation_1 = require("nmr-correlation");
const react_1 = require("react");
const Concatenation_1 = require("../../../../data/utilities/Concatenation");
const FindUtilities_1 = require("../../../../data/utilities/FindUtilities");
const ContextMenu_1 = __importDefault(require("../../../elements/ContextMenu"));
const Modal_1 = require("../../../elements/popup/Modal");
const highlight_1 = require("../../../highlight");
const Utilities_1 = require("../utilities/Utilities");
const useInView_1 = __importDefault(require("../utilities/useInView"));
const EditLinkModal_1 = __importDefault(require("./editLink/EditLinkModal"));
function AdditionalColumnField({ rowCorrelation, columnCorrelation, commonLinks, correlations, spectraData, onEdit, }) {
    const contextRef = (0, react_1.useRef)();
    const modal = (0, Modal_1.useModal)();
    const highlightIDsCommonLinks = (0, react_1.useMemo)(() => {
        return commonLinks.flatMap((link) => {
            const ids = [];
            if (link.pseudo === false) {
                ids.push(link.signal.id, (0, Concatenation_1.buildID)(link.signal.id, 'Crosshair'));
                const _id = (0, FindUtilities_1.findRangeOrZoneID)(spectraData, link.experimentID, link.signal.id, true);
                if (_id) {
                    ids.push(_id);
                }
            }
            return ids;
        });
    }, [commonLinks, spectraData]);
    const highlightCommonLinks = (0, highlight_1.useHighlight)(highlightIDsCommonLinks);
    const mouseEnterHandler = (0, react_1.useCallback)((event) => {
        event.currentTarget.focus();
        highlightCommonLinks.show();
    }, [highlightCommonLinks]);
    const mouseLeaveHandler = (0, react_1.useCallback)((event) => {
        event.currentTarget.blur();
        highlightCommonLinks.hide();
    }, [highlightCommonLinks]);
    const contextMenuHandler = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        contextRef.current.handleContextMenu(e);
    }, [contextRef]);
    const handleEditPseudoHSQC = (0, react_1.useCallback)((action, link) => {
        const pseudoLinkCountHSQC = rowCorrelation.link.filter((_link) => (_link.experimentType === 'hsqc' ||
            _link.experimentType === 'hmqc') &&
            _link.pseudo === true).length;
        let _correlationDim1;
        let _correlationDim2;
        if (action === 'add') {
            const commonPseudoLink = (0, nmr_correlation_1.buildLink)({
                experimentType: 'hsqc',
                experimentID: (0, uuid_1.v4)(),
                atomType: [columnCorrelation.atomType, rowCorrelation.atomType],
                id: (0, uuid_1.v4)(),
                pseudo: true,
                signal: { id: (0, uuid_1.v4)(), sign: 0 }, // pseudo signal
            });
            _correlationDim1 = (0, Utilities_1.cloneCorrelationAndEditLink)(columnCorrelation, commonPseudoLink, 'x', 'add');
            _correlationDim2 = (0, Utilities_1.cloneCorrelationAndEditLink)(rowCorrelation, commonPseudoLink, 'y', 'add');
            // increase number of attached protons if no value was specified manually before
            if (!_correlationDim2.edited.protonsCount) {
                _correlationDim2.protonsCount = [pseudoLinkCountHSQC + 1];
            }
        }
        else {
            _correlationDim1 = (0, Utilities_1.cloneCorrelationAndEditLink)(columnCorrelation, link, 'x', 'remove');
            _correlationDim2 = (0, Utilities_1.cloneCorrelationAndEditLink)(rowCorrelation, link, 'y', 'remove');
            // decrease number of attached protons if no value was specified manually before
            if (!_correlationDim2.edited.protonsCount) {
                _correlationDim2.protonsCount =
                    pseudoLinkCountHSQC - 1 > 0 ? [pseudoLinkCountHSQC - 1] : [];
            }
        }
        onEdit([_correlationDim1, _correlationDim2], action, link, {
            skipDataUpdate: true,
        });
    }, [columnCorrelation, onEdit, rowCorrelation]);
    const contextMenu = (0, react_1.useMemo)(() => {
        // allow the edition of correlations
        const commonLinksMenu = commonLinks.flatMap((commonLink) => {
            const commonLinkContextMenuLabel = `${(0, Utilities_1.getAbbreviation)(commonLink)} (${commonLink.signal.x?.delta ? commonLink.signal.x.delta.toFixed(2) : '?'}, ${commonLink.signal.y?.delta ? commonLink.signal.y.delta.toFixed(2) : '?'})${commonLink.edited?.moved === true ? '[MOVED]' : ''}`;
            return commonLink.pseudo === false
                ? [
                    {
                        label: `edit ${commonLinkContextMenuLabel}`,
                        onClick: () => {
                            highlightCommonLinks.hide();
                            modal.show((0, jsx_runtime_1.jsx)(EditLinkModal_1.default, { onClose: () => modal.close(), onEdit: onEdit, link: commonLink, correlationDim1: columnCorrelation, correlationDim2: rowCorrelation, correlations: correlations }), {
                                position: Modal_1.positions.MIDDLE_RIGHT,
                                isBackgroundBlur: false,
                            });
                        },
                    },
                ]
                : [];
        });
        // allow addition or removal of a pseudo HSQC link between pseudo heavy atom and proton
        const commonPseudoLinkHSQC = commonLinks.find((commonLink) => commonLink.pseudo === true && commonLink.experimentType === 'hsqc');
        if (rowCorrelation.pseudo === true) {
            if (commonPseudoLinkHSQC) {
                commonLinksMenu.push({
                    label: 'remove pseudo HSQC',
                    onClick: () => handleEditPseudoHSQC('remove', commonPseudoLinkHSQC),
                });
            }
            else {
                commonLinksMenu.push({
                    label: 'add pseudo HSQC',
                    onClick: () => handleEditPseudoHSQC('add'),
                });
            }
        }
        return commonLinksMenu;
    }, [
        columnCorrelation,
        commonLinks,
        correlations,
        handleEditPseudoHSQC,
        highlightCommonLinks,
        modal,
        onEdit,
        rowCorrelation,
    ]);
    const contentLabel = (0, react_1.useMemo)(() => commonLinks.map((commonLink, i) => ((0, jsx_runtime_1.jsxs)("label", { children: [(0, jsx_runtime_1.jsx)("label", { style: {
                    color: commonLink.pseudo === true || commonLink.edited?.moved === true
                        ? 'blue'
                        : 'black',
                }, children: (0, Utilities_1.getAbbreviation)(commonLink) }), i < commonLinks.length - 1 && (0, jsx_runtime_1.jsx)("label", { children: "/" })] }, commonLink.id))), [commonLinks]);
    const isInViewRow = (0, useInView_1.default)({ correlation: rowCorrelation });
    const isInViewColumn = (0, useInView_1.default)({ correlation: columnCorrelation });
    return ((0, jsx_runtime_1.jsxs)("td", { onContextMenu: (e) => {
            if (contextMenu.length > 0) {
                contextMenuHandler(e);
            }
        }, style: {
            backgroundColor: highlightCommonLinks.isActive
                ? '#ff6f0057'
                : isInViewColumn || isInViewRow
                    ? '#f5f5dc'
                    : 'inherit',
        }, title: getTitle(commonLinks), onMouseEnter: mouseEnterHandler, onMouseLeave: mouseLeaveHandler, children: [contentLabel, (0, jsx_runtime_1.jsx)(ContextMenu_1.default, { ref: contextRef, context: contextMenu })] }));
}
function getTitle(commonLinks) {
    if (commonLinks?.length === 0) {
        return '';
    }
    return [
        ...new Set(commonLinks?.link?.map((link) => {
            return link.experimentType.toUpperCase();
        })),
    ].join('/');
}
exports.default = AdditionalColumnField;
